<template>
<div class="contents">
  <div class="reservation-box">
    <table class="tbl">
      <colgroup>
        <col style="width: 7%;">
        <col style="width: 13%;">
        <col style="width: 10%;">
        <col style="width: 10%;">
        <col style="width: 11%;">
        <col style="width: 7%;">
        <col style="width: 12%;">
        <col style="width: 14%;">
        <col style="width: 6%;">
        <col style="width: 10%;">
      </colgroup>
      <thead>
        <tr>
          <th class="tbl-header">使用有無</th>
          <th class="tbl-header">ドライバー</th>
          <th class="tbl-header">稼働開始</th>
          <th class="tbl-header">稼働終了</th>
          <th class="tbl-header">サイズ</th>
          <th class="tbl-header">車両ID</th>
          <th class="tbl-header">車両名</th>
          <th class="tbl-header">登録番号</th>
          <th class="tbl-header">積載量</th>
          <th class="tbl-header">所有者</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in vehicleRows" :key="row.vehicleId">
          <td class="tbl-cell checkbox-cell">
            <input v-model="row.isSelected" type="checkbox" />
          </td>
          <td class="tbl-cell">
            <select v-model="row.driverUserId" class="table-select">
              <option :value="''"></option>
              <option v-for="driver in driverRows" :key="driver.id" :value="driver.id">
                {{ driver.name }}
              </option>
            </select>
          </td>
          <td class="tbl-cell">
            <select v-model="row.operationStartTime" class="table-select">
              <option v-for="option in operationTimeOptions" :key="`st-${option.value}`" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </td>
          <td class="tbl-cell">
            <select v-model="row.operationEndTime" class="table-select">
              <option v-for="option in operationTimeOptions" :key="`et-${option.value}`" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </td>
          <td class="tbl-cell">
            <select v-model="row.sizeType" class="table-select">
              <option :value="SizeType.ALL">all</option>
              <option :value="SizeType.ONETIME_S_M">単,S,M</option>
            </select>
          </td>
          <td class="tbl-cell">{{ row.vehicleId }}</td>
          <td class="tbl-cell">{{ row.vehicleName }}</td>
          <td class="tbl-cell">{{ row.registrationNumber }}</td>
          <td class="tbl-cell">{{ row.payloadCapacity }}</td>
          <td class="tbl-cell">{{ row.ownerName }}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="summary-box">
    <p class="summary-row">
      <span class="summary-label item2">配車日付</span>
      <my-calendar
        v-model="dispatchDate"
        width="170px"
        :readonly="true"
        @input="onDispatchDateChanged"
      ></my-calendar>
    </p>
    <p class="summary-row">
      <span class="summary-label item2">車両台数</span>
      <span class="summary-value">{{ selectedVehicleCount }}台</span>
    </p>
  </div>

  <div class="action-area">
    <button class="submit1 bg_blue" :disabled="!isDispatchDateValid" @click="saveReservation">
      配車予約
    </button>

    <div class="message-area">
      <div v-if="errorMessage" class="err-msg-area">
        <span class="font_orange">{{ errorMessage }}</span>
      </div>
      <div v-if="successMessage" class="success-msg-area">
        <span class="font_blue">{{ successMessage }}</span>
      </div>
    </div>
  </div>

  <p class="reservation-status">
    <span class="reservation-label item2">予約状況</span>
    <span>{{ reservedSummaryText }}</span>
  </p>
</div>
</template>

<script>
import { AdminPageType } from '@/consts/admin'
import adminDispatchReservationApi from '@/api/adminDispatchReservation'
import { getApiErrorMessage } from '@/utils/apiErrorHelper'
import { useAdminPageAccess } from '@/composables/useAdminPageAccess'
import {
  SizeType,
  OPERATION_HOUR_START,
  OPERATION_HOUR_END,
} from '@/consts/dispatchReservation'

export default {
  name: 'admin-dispatch-reservations',
  setup(props) {
    const { ensureManagerAccess } = useAdminPageAccess(props.adminType)
    return { ensureManagerAccess }
  },
  props: {
    adminType: {
      type: String,
      default: AdminPageType.ADMIN
    }
  },
  data() {
    return {
      SizeType,
      errorMessage: '',
      successMessage: '',
      dispatchDate: '',
      dateRange: {
        min: '',
        max: '',
      },
      driverRows: [],
      vehicleRows: [],
      reservationState: {
        reservedAt: null
      }
    }
  },
  computed: {
    isDispatchDateValid() {
      return this.isValidDispatchDateInRange(this.dispatchDate)
    },
    selectedVehicleCount() {
      return this.vehicleRows.filter(row => row.isSelected).length
    },
    operationTimeOptions() {
      const options = []
      for (let hour = OPERATION_HOUR_START; hour <= OPERATION_HOUR_END; hour++) {
        const hourLabel = String(hour).padStart(2, '0')
        options.push({
          value: `${hourLabel}:00:00`,
          label: `${hourLabel}:00`
        })
      }
      return options
    },
    reservedSummaryText() {
      if (!this.reservationState.reservedAt) {
        return '未予約'
      }

      const dispatchDateText = this.dispatchDate
        ? this.toDisplayDateText(this.dispatchDate)
        : ''
      const [operationDate, operationTime] = this.reservationState.reservedAt.split(' ')
      if (!operationDate || !operationTime) {
        return `予約済み: ${dispatchDateText}(操作日: ${this.reservationState.reservedAt})`
      }

      const operationDateText = operationDate.replace(/-/g, '/')
      const operationTimeText = operationTime.slice(0, 5)
      return `予約済み: ${dispatchDateText}(操作日: ${operationDateText} ${operationTimeText})`
    }
  },
  async mounted() {
    try {
      const canAccessPage = await this.ensureManagerAccess()
      if (!canAccessPage) return

      const todayDate = this.toDateText(new Date())
      const tomorrowDate = this.toDateText(this.addDays(new Date(), 1))
      this.dateRange = {
        min: todayDate,
        max: tomorrowDate,
      }
      this.dispatchDate = tomorrowDate
      await this.fetchReservationRows()
    } catch (error) {
      this.errorMessage = '配車予約画面の初期化に失敗しました。'
    }
  },
  methods: {
    toDateText(dateObject) {
      const year = dateObject.getFullYear()
      const month = String(dateObject.getMonth() + 1).padStart(2, '0')
      const day = String(dateObject.getDate()).padStart(2, '0')
      return `${year}/${month}/${day}`
    },
    addDays(dateObject, addDayCount) {
      const nextDate = new Date(dateObject)
      nextDate.setDate(nextDate.getDate() + addDayCount)
      return nextDate
    },
    normalizeDateText(dateText) {
      if (!dateText) {
        return ''
      }

      return String(dateText).replace(/\//g, '-')
    },
    toDisplayDateText(dateText) {
      return String(dateText || '').replace(/-/g, '/')
    },
    isValidDispatchDateInRange(dateText) {
      const apiDateText = this.normalizeDateText(dateText)
      const minDate = this.normalizeDateText(this.dateRange.min)
      const maxDate = this.normalizeDateText(this.dateRange.max)

      return apiDateText >= minDate && apiDateText <= maxDate
    },
    buildDefaultVehicleRow(vehicle) {
      return {
        vehicleId: vehicle.vehicle_id,
        vehicleName: vehicle.vehicle_name,
        registrationNumber: vehicle.registration_number,
        payloadCapacity: vehicle.payload_capacity,
        ownerName: vehicle.owner_name,
        isSelected: false,
        driverUserId: '',
        operationStartTime: '12:00:00',
        operationEndTime: '22:00:00',
        sizeType: SizeType.ALL,
      }
    },
    async fetchReservationRows() {
      this.errorMessage = ''
      this.successMessage = ''

      try {
        const response = await adminDispatchReservationApi.getByDate(this.normalizeDateText(this.dispatchDate))
        const responseBody = response.data

        this.driverRows = responseBody.drivers.map(driver => ({
          id: driver.id,
          name: `${driver.name1}${driver.name2}`
        }))

        this.vehicleRows = responseBody.vehicles.map(vehicle => {
          const defaultRow = this.buildDefaultVehicleRow(vehicle)
          if (!vehicle.is_reserved) {
            return defaultRow
          }

          return {
            ...defaultRow,
            isSelected: true,
            driverUserId: vehicle.driver_user_id || '',
            operationStartTime: vehicle.operation_start_time || '12:00:00',
            operationEndTime: vehicle.operation_end_time || '22:00:00',
            sizeType: vehicle.size_type || SizeType.ALL,
          }
        })

        this.reservationState = {
          reservedAt: responseBody.reservation.reserved_at
        }
      } catch (error) {
        this.errorMessage = getApiErrorMessage(error, '配車予約情報の取得に失敗しました。')
      }
    },
    async onDispatchDateChanged() {
      await this.fetchReservationRows()
    },
    async saveReservation() {
      this.errorMessage = ''
      this.successMessage = ''

      if (!this.isDispatchDateValid) {
        return
      }

      const selectedRows = this.vehicleRows.filter(row => row.isSelected)
      if (selectedRows.length === 0) {
        this.errorMessage = '車両を1台以上選択してください。'
        return
      }

      const invalidRow = selectedRows.find(row => row.operationStartTime > row.operationEndTime)
      if (invalidRow) {
        this.errorMessage = '稼働開始は稼働終了以前を指定してください。'
        return
      }

      const payload = {
        dispatch_date: this.normalizeDateText(this.dispatchDate),
        reservation_vehicles: selectedRows.map(row => ({
          vehicle_id: row.vehicleId,
          driver_user_id: row.driverUserId || null,
          operation_start_time: row.operationStartTime,
          operation_end_time: row.operationEndTime,
          size_type: row.sizeType,
        }))
      }

      try {
        const response = await adminDispatchReservationApi.save(payload)
        const responseBody = response.data
        this.successMessage = responseBody.message || '配車計画の予約が完了しました'
        this.reservationState = {
          reservedAt: responseBody.reserved_at
        }
      } catch (error) {
        this.errorMessage = getApiErrorMessage(error, '配車予約の保存に失敗しました。')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.contents {
  width: 95%;
  margin: 20px auto 0;
  min-height: 400px;
}

.reservation-box {
  width: 100%;
  max-width: 1160px;
  overflow-x: auto;
}

.tbl {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 13px;
  background-color: #ffffff;

  .tbl-cell,
  .tbl-header {
    border: 1px solid #b8c0cc;
    padding: 4px 6px;
    vertical-align: middle;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tbl-header {
    background-color: #dce7f8;
    font-weight: 700;
    text-align: left;
    white-space: nowrap;
  }
}

.checkbox-cell {
  text-align: center;
}

.table-select {
  width: 100%;
  min-width: 70px;
  font-size: 12px;
}

.summary-box {
  margin-top: 12px;
  margin-bottom: 72px;
}

.summary-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 8px;
  font-size: 18px;
  line-height: 1.4;
}

.summary-label {
  display: inline-block;
  min-width: 110px;
}

.summary-value {
  font-size: 18px;
}

.action-area {
  margin-top: 6px;
  text-align: center;

  .submit1 {
    width: 190px;

    &:disabled {
      background-color: #b0b8c1;
      cursor: not-allowed;
    }
  }
}

.message-area {
  min-height: 28px;
  margin-top: 10px;
}

.err-msg-area,
.success-msg-area {
  margin-top: 0;
}

.reservation-status {
  margin-top: 18px;
  min-height: 24px;
  font-size: 18px;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  padding: 0;
}

.reservation-label {
  display: inline-block;
  min-width: 110px;
  margin: 0;
}

@media (max-width: 960px) {
  .tbl {
    min-width: 980px;
  }

  .summary-box {
    margin-bottom: 52px;
  }
}
</style>
